import { useIntl } from 'react-intl';
import { Input } from 'sonata-react-components';

import { ColorPicker } from '@/components/common/ColorPicker/ColorPicker';
import { groupingMessages } from '../groupingMessages';

import './groupForm.scss';

import type { GroupNameError } from '../../domain/grouping.types';

/** Color preselected for new groups (matches the design). */
export const DEFAULT_GROUP_COLOR = 'var(--color-charts-li-pink-400)';

export interface GroupFormProps {
  color: string;
  id: string;
  name: string;
  /** Only `duplicate` is shown to the user; an empty name just keeps confirm disabled. */
  nameError: GroupNameError | null;
  /** Shows the name without letting the user change it (e.g. cartographic groups). Defaults to `false`. */
  nameReadOnly?: boolean;
  onColorChange: (color: string) => void;
  onNameChange: (name: string) => void;
}

/** Group name + color fields. */
export function GroupForm({
  color,
  id,
  name,
  nameError,
  nameReadOnly = false,
  onColorChange,
  onNameChange
}: Readonly<GroupFormProps>) {
  const { formatMessage } = useIntl();
  const isDuplicate = nameError === 'duplicate';

  return (
    <div className="groupForm">
      <div className="groupForm__name">
        <Input
          description={isDuplicate ? formatMessage(groupingMessages.groupNameExists) : ''}
          id={`${id}-groupName`}
          inputStatus={isDuplicate ? 'wrong' : 'default'}
          label={formatMessage(groupingMessages.groupName)}
          name="groupName"
          onChange={value => onNameChange(String(value ?? ''))}
          placeholder={formatMessage(groupingMessages.groupNamePlaceholder)}
          readOnly={nameReadOnly}
          shape="squareUI"
          type="text"
          value={name}
        />
      </div>
      <ColorPicker
        className="groupForm__color"
        label={formatMessage(groupingMessages.groupColor)}
        mode="expanded-line"
        onColorChange={onColorChange}
        selected={color}
      />
    </div>
  );
}
